import { Div, Icon, InputSearchIcon, Label, X } from '@vgrubs/components';
import { forwardRef, useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';

type AdminInputProps = {
  onXButtonClick?: (e: React.MouseEvent<HTMLElement>) => void;
  shadowDesign?: boolean;
  $textCenter?: boolean;
  expanded?: boolean;
  maxWidth?: string;
  maxWidthSM?: string;
};

const AdminInputStyled = styled.input<AdminInputProps>`
  width: ${({ expanded, maxWidth }) =>
    expanded ? (maxWidth ? maxWidth : '300px') : '40px'};
  border: 1px solid ${({ theme }) => theme.darkGray}80;
  /* flex: ${({ expanded }) => (expanded ? 1 : 0)}; */
  border-radius: 200px;
  font-size: 15px;
  line-height: 18px;
  padding: ${({ expanded }) => (expanded ? '0 40px 0 24px' : '0')};
  display: flex;
  align-items: center;
  height: ${({ theme }) => theme.input.height};
  -moz-appearance: none; /* Firefox */
  -webkit-appearance: none; /* Safari and Chrome */
  appearance: none;
  background: none;
  transition: ${({ theme }) => theme.transitions.ease300ms};
  position: relative;
  top: 0;
  right: 0;
  z-index: ${({ expanded }) => (expanded ? 1 : 0)};

  opacity: ${({ expanded }) => (expanded ? 1 : 0)};

  :disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  ${({ shadowDesign, theme }) =>
    shadowDesign &&
    `
  background: ${theme.white};
  border: 1px solid #e3e3e3;
  box-shadow: ${theme.shadows.shadow1};

  `}

  ${({ $textCenter }) => $textCenter && `text-align: center`};

  @media (max-width: 800px) {
    width: ${({ expanded, maxWidthSM }) =>
      expanded ? (maxWidthSM ? maxWidthSM : '200px') : '40px'};
    padding: ${({ expanded }) => (expanded ? '0 40px 0 16px' : '0')};
    font-size: 14px;
  }

  :focus {
    outline: none !important;
    border: 1px solid ${({ theme }) => theme.primary};
  }

  ::-webkit-inner-spin-button,
  ::-webkit-outer-spin-button {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    margin: 0;
  }

  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
`;

type BaseProps = AdminInputProps & {
  maxWidth?: string;
  maxWidthSM?: string;
  mb?: string;
  label?: string;
  tooltip?: string;
  maxLength?: number;
  noMaxLengthLabel?: boolean;
  tooltipWidth?: string;
  tooltipWidthSM?: string;
};

type InputProps = BaseProps & React.InputHTMLAttributes<HTMLInputElement>;

type SearchInputComponentProps = InputProps;

export const SearchInput = forwardRef<
  HTMLInputElement,
  SearchInputComponentProps
>(function SearchInput({
  mb,
  label,
  maxWidth,
  maxWidthSM,
  $textCenter,
  shadowDesign,
  ...props
}) {
  const [expanded, setExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Through ThemeProvider so the focused ring follows the app's primary.
  const theme = useTheme();

  const handleExpand = () => {
    if (expanded) {
      setExpanded(false);
      inputRef.current?.blur();
    } else {
      setExpanded(true);
      inputRef.current?.focus();
    }
  };

  useEffect(() => {
    if (inputRef.current?.value !== '' && !expanded) {
      setExpanded(true);
    }
  }, [expanded, inputRef.current?.value]);

  const length = props.value?.toString().length ?? 0;

  const hasValue = length > 0;

  const handleClear = () => {
    props.onChange?.({
      target: { value: '' },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  return (
    <Div
      $relative
      $maxWidth={maxWidth}
      $maxWidthSM={maxWidthSM}
      $mb={mb}
      $minWidth="40px"
    >
      {label && <Label label={label} $textCenter={$textCenter} />}

      <AdminInputStyled
        onFocus={() => {
          if (expanded) return;
          setExpanded(true);
        }}
        onBlur={() => {
          if (inputRef.current?.value === '') {
            setExpanded(false);
          }
        }}
        ref={inputRef as React.Ref<HTMLInputElement>}
        $textCenter={$textCenter}
        shadowDesign={shadowDesign}
        expanded={expanded}
        maxWidth={maxWidth}
        maxWidthSM={maxWidthSM}
        {...(props as InputProps)}
      />

      <Icon
        style={{ position: 'absolute', right: 0, top: 0, zIndex: 2 }}
        $background={theme.white}
        $backgroundSize={40}
        $pointer
        $shadow
        onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
        onClick={!hasValue ? handleExpand : handleClear}
        $borderColor={expanded ? theme.primary : theme.mediumGray}
      >
        {!hasValue ? (
          <InputSearchIcon />
        ) : (
          <Icon $pointer $size="12" onClick={() => {}}>
            <X fill={theme.darkGray} />
          </Icon>
        )}
      </Icon>
    </Div>
  );
});
